/**
 * CSS Styles and Typography Configuration for Readium 2 EPUB Viewer.
 *
 * This file centralizes all injectable CSS styles and font size settings
 * used by the Readium 2 navigator to render EPUB content.
 */

// Import accessible fonts CSS as raw string (shared with fonts.scss)
import accessibleFontsCssRaw from '../../../../../sass/accessible-reader-fonts.css?raw';

// ─────────────────────────────────────────────────────────────────────────────
// Font Size Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Font size limits for EPUB zoom (in REM units, where 1 = 100%).
 * These values control the min/max zoom levels available to users.
 */
export const FONT_SIZE_MIN = 0.75;      // 75%
export const FONT_SIZE_MAX = 4;         // 400%
export const FONT_SIZE_STEP = 0.25;     // 25% increment per click
export const FONT_SIZE_DEFAULT = 1;     // 100%

/**
 * API format conversion constants.
 * The API uses integers where 120 = 100% (base value).
 * This allows for finer granularity without floating point issues.
 *
 * Conversion formulas:
 *   - REM to API: remValue * API_FONT_SIZE_BASE
 *   - API to REM: apiValue / API_FONT_SIZE_BASE
 *
 * Examples:
 *   - 75% (0.75 rem) = 90 API
 *   - 100% (1.0 rem) = 120 API
 *   - 400% (4.0 rem) = 480 API
 */
export const API_FONT_SIZE_BASE = 120;  // API value for 100%
export const API_FONT_SIZE_MIN = 90;    // API minimum (75%)
export const API_FONT_SIZE_MAX = 480;   // API maximum (400%)

// ─────────────────────────────────────────────────────────────────────────────
// Line Height Configuration
// ─────────────────────────────────────────────────────────────────────────────

export const LINE_HEIGHT_COMPACT = 1.2;
export const LINE_HEIGHT_NORMAL = 1.5;
export const LINE_HEIGHT_WIDE = 2.0;
export const LINE_HEIGHT_DEFAULT = LINE_HEIGHT_NORMAL;

// ─────────────────────────────────────────────────────────────────────────────
// Margins Configuration (pageGutter values in pixels)
// ─────────────────────────────────────────────────────────────────────────────

export const MARGINS_MINIMUM = 5;
export const MARGINS_NORMAL = 20;
export const MARGINS_MAXIMUM = 60;
// Default must be MARGINS_MINIMUM because Readium2's web navigator computes
// column widths using the initial pageGutter value. Starting with a larger
// gutter and then reducing it dynamically causes text clipping, as the
// navigator does not fully recalculate the column layout for the smaller value.
// See: https://github.com/readium/readium-css/issues/2
export const MARGINS_DEFAULT = MARGINS_MINIMUM;

// ─────────────────────────────────────────────────────────────────────────────
// Injectable CSS Styles
// ─────────────────────────────────────────────────────────────────────────────

/**
 * CSS injected into EPUB content frames via injectables.rules.
 * Adds margins to prevent pagination buttons from overlapping content in scroll mode.
 *
 * Applied to: All frames when scroll mode is active
 */
export const SCROLL_MODE_MARGINS_CSS = `
body {
    margin-bottom: 100px !important;
    margin-top: 50px !important;
}
`;

/**
 * Generates accessible fonts CSS with absolute URLs for EPUB iframes.
 * Uses the shared CSS from accessible-reader-fonts.css, replacing relative
 * URLs with absolute ones since iframes load content from blob: origins.
 *
 * @param {string} baseUrl - The origin URL (e.g., 'https://volpe.localhost')
 * @returns {string} CSS with @font-face declarations and inheritance override
 */
export const getAccessibleFontsCss = (baseUrl) => {
    // Replace relative font URLs with absolute URLs for iframe context
    const fontsWithAbsoluteUrls = accessibleFontsCssRaw.replace(
        /url\(\.\.\/fonts\//g,
        `url(${baseUrl}/volpe-assets/fonts/`
    );

    // Add override rule for ReadiumCSS 'revert' behavior
    return `${fontsWithAbsoluteUrls}
/* Override ReadiumCSS 'revert' rule - force inheritance for custom fonts */
:root[style*="--USER__fontFamily"] body *:not(code):not(var):not(kbd):not(samp) {
    font-family: inherit !important;
}`;
};

export const COVER_FRAME_CSS = `
html, body {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    height: 100% !important;
    min-height: 100vh !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
}
body,
body.fullpage,
body#cover,
body[epub\\:type],
body.calibre {
    height: 100% !important;
    max-width: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}
section.cover,
section.cover.fullpage,
.cover,
.capa,
[epub\\:type="cover"],
.paginacompleta {
    height: auto !important;
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 !important;
    padding: 0 !important;
}
img.coverimage, img[role="doc-cover"], section.cover img, .cover img, .capa img, img.capa {
    max-height: var(--cover-max-height, 100vh) !important;
    max-width: 100% !important;
    width: auto !important;
    height: auto !important;
    object-fit: contain !important;
    margin: 0 auto !important;
}
/* SVG-wrapped cover images (common in some EPUB generators) */
svg {
    height: var(--cover-max-height, 100vh) !important;
    width: auto !important;
    max-width: 100% !important;
    display: block !important;
    margin: 0 auto !important;
}
`;
